import { useTheme } from "next-themes";
import { useEffect } from "react";
import { Toaster as Sonner, toast } from "sonner";
import { Check, AlertCircle, AlertTriangle, Info } from "lucide-react";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();


  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      closeButton
      expand
      visibleToasts={3}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-card group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-xl group-[.toaster]:ring-1 group-[.toaster]:ring-border/20 [&[data-type]]:!border-l-0",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          closeButton:
            "group-[.toast]:!bg-background group-[.toast]:!text-foreground group-[.toast]:!border-border",
        },
      }}
      {...props}
    />
  );
};

// Sorvaine Semantic Toast Helpers with duration behavior + click-to-dismiss
// Note: Click-to-dismiss is handled via CSS pointer-events and global click handler

interface ToastOptions {
  description?: string;
  id?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Success: 3 second auto-dismiss (quick confirmation)
const successToast = (message: string, options?: ToastOptions | string) => {
  const opts = typeof options === 'string' ? { description: options } : options;
  return toast.success(message, {
    description: opts?.description,
    duration: 3000,
    id: opts?.id,
    action: opts?.action
  });
};

// Error: Persist until dismissed
const errorToast = (message: string, options?: ToastOptions | string) => {
  const opts = typeof options === 'string' ? { description: options } : options;
  return toast.error(message, {
    description: opts?.description,
    duration: 10000,
    id: opts?.id,
    action: opts?.action
  });
};

// Warning: 5 second auto-dismiss
const warningToast = (message: string, options?: ToastOptions | string) => {
  const opts = typeof options === 'string' ? { description: options } : options;
  return toast.warning(message, {
    description: opts?.description,
    duration: 5000,
    id: opts?.id,
    action: opts?.action
  });
};

// Info: 4 second auto-dismiss
const infoToast = (message: string, options?: ToastOptions | string) => {
  const opts = typeof options === 'string' ? { description: options } : options;
  return toast.info(message, {
    description: opts?.description,
    duration: 4000,
    id: opts?.id,
    action: opts?.action
  });
};

export { Toaster, toast, successToast, errorToast, warningToast, infoToast };